import { ApiCore } from '@/services/api/utils/core.js'
import { apiProvider } from '@/services/api/utils/provider.js'
import axios from 'axios'
import { handleResponse, handleError } from '@/services/api/utils/response.js'

const url = 'data-element-versions'

const api = new ApiCore({
	index: true,
	url: url
})

/**
 * GET /data-element-versions?dataElementId=&version=&status=&type=&all=true&currentPage=&pageSize=
 * List data element versions with filters and pagination
 * Response: { data: { items: DataElementVersion[], meta: { currentPage, pageSize, pageCount, count } } }
 */
api.index = (params) => {
	return axios
		.get(`${apiProvider.BASE_URL}/${url}?${params}`)
		.then(handleResponse)
		.catch(handleError);
}

/**
 * GET /data-element-versions?dataElementId={id}&all=true
 * Get all versions for a specific data element (for detail/version-history page)
 */
api.getAllVersions = (dataElementId) => {
	return axios
		.get(`${apiProvider.BASE_URL}/${url}?dataElementId=${dataElementId}&all=true`)
		.then(handleResponse)
		.catch(handleError);
}

/**
 * GET /data-element-versions/:dataElementId/versions/:version
 * Get a single DataElementVersion by dataElementId and version number
 * Response: DataElementVersion object
 */
api.getByVersion = (dataElementId, version) => {
	return axios
		.get(`${apiProvider.BASE_URL}/${url}/${dataElementId}/versions/${version}`)
		.then(handleResponse)
		.catch(handleError);
}

/**
 * POST /data-element-versions
 * Create initial data element versions (batch)
 * Body: IDataElementVersion[] (id, name, schema, version, status, changeType, changeDescription, type, expiry)
 * Response: "Successfully Inserted : <ids>"
 */
api.createInitial = (payload) => {
	return axios
		.post(`${apiProvider.BASE_URL}/${url}`, payload)
		.then(handleResponse)
		.catch(handleError);
}

/**
 * POST /data-element-versions/:dataElementId/versions
 * Create new version for existing data element
 * Body: { name, schema, querySchema, version, status, changeType, changeDescription, expiry, type }
 * Response: DataElementVersion object
 */
api.createNewVersion = (dataElementId, payload) => {
	return axios
		.post(`${apiProvider.BASE_URL}/${url}/${dataElementId}/versions`, payload)
		.then(handleResponse)
		.catch(handleError);
}

/**
 * POST /data-element-versions/:dataElementId/versions/:version/promote
 * Promote version status: DRAFT → ACTIVE → DEPRECATED → RETIRED
 * Body: { targetStatus: 'ACTIVE' | 'DEPRECATED' | 'RETIRED', expiryDate?: ISO8601 string }
 * Response: Updated DataElementVersion object
 */
api.promoteVersion = (dataElementId, version, payload) => {
	return axios
		.post(`${apiProvider.BASE_URL}/${url}/${dataElementId}/versions/${version}/promote`, payload)
		.then(handleResponse)
		.catch(handleError);
}

/**
 * DELETE /data-element-versions/:dataElementId/versions/:version
 * Delete a DRAFT version. Response: { success: true }
 */
api.deleteVersion = (dataElementId, version) => {
	return axios
		.delete(`${apiProvider.BASE_URL}/${url}/${dataElementId}/versions/${version}`)
		.then(handleResponse)
		.catch(handleError);
}

/**
 * PATCH /data-element-versions/:dataElementId/versions/:version/version-number
 * Edit a DRAFT version's version number. Body: { newVersion }.
 * Returns 409 on clash. Custom catch preserves HTTP status so the hook can
 * branch on conflict (the shared handleError drops the status).
 */
api.updateVersionNumber = (dataElementId, version, newVersion) => {
	return axios
		.patch(`${apiProvider.BASE_URL}/${url}/${dataElementId}/versions/${version}/version-number`, { newVersion })
		.then(handleResponse)
		.catch((error) => {
			const status = error?.response?.status;
			const innerError = error?.response?.data?.data?.error
				|| error?.response?.data?.error;
			const message = innerError?.message
				|| (typeof innerError === 'string' ? innerError : null)
				|| error?.message
				|| 'Failed to update version number';
			return [{ status, message, response: error?.response }, undefined];
		});
}

/**
 * GET /data-element-versions/:dataElementId/versions/:version/impact-analysis
 * Get impact analysis for a version
 * Response: { riskScore, affectedOrganizations, subscribedOrganizations, organizations: [{ orgId, name, orgType, industry, role }], integrationsCount, estimatedMigrationTime, breakingChanges, recommendations }
 * where role is one of 'consumer' | 'provider' | 'both' (consumer = receives data, provider = sends data)
 */
api.getImpactAnalysis = (dataElementId, version) => {
	return axios
		.get(`${apiProvider.BASE_URL}/${url}/${dataElementId}/versions/${version}/impact-analysis`)
		.then(handleResponse)
		.catch(handleError);
}

/**
 * PATCH /data-element-versions/:dataElementId/versions/:version
 * Patch a DRAFT version (autosave schema changes)
 * Body: { schema: {...}, updatedAt: "ISO string" }
 * Response: Updated DataElementVersion object
 * Returns 409 on optimistic-locking conflict
 *
 * Custom error handler preserves HTTP status so callers can branch on 409
 * (the shared handleError unwraps to the inner error body and drops status).
 */
api.patchVersion = (dataElementId, version, payload) => {
	return axios
		.patch(`${apiProvider.BASE_URL}/${url}/${dataElementId}/versions/${version}`, payload)
		.then(handleResponse)
		.catch((error) => {
			const status = error?.response?.status;
			const innerError = error?.response?.data?.data?.error
				|| error?.response?.data?.error;
			const message = innerError?.message
				|| (typeof innerError === 'string' ? innerError : null)
				|| error?.message
				|| 'Patch failed';
			return [{ status, message, response: error?.response }, undefined];
		});
}

/**
 * POST /data-elements
 * Create a new data element (parent record)
 * Body: { name, type }
 * Response: DataElement object
 */
api.createDataElement = (payload) => {
	return axios
		.post(`${apiProvider.BASE_URL}/data-elements`, payload)
		.then(handleResponse)
		.catch(handleError);
}

/**
 * GET /data-element-version-comments?dataElementId={id}&versionId={version}
 * Get comments for a specific version
 */
api.getComments = (dataElementId, versionId) => {
	return axios
		.get(`${apiProvider.BASE_URL}/data-element-version-comments?dataElementId=${dataElementId}&versionId=${versionId}`)
		.then(handleResponse)
		.catch(handleError);
}

/**
 * POST /data-element-version-comments
 * Create a comment on a version
 * Body: { dataElementId, versionId, commentText, parentCommentId? }
 */
api.createComment = (payload) => {
	return axios
		.post(`${apiProvider.BASE_URL}/data-element-version-comments`, payload)
		.then(handleResponse)
		.catch(handleError);
}

/**
 * POST /usecasepersonas
 * Attach a data element to a use case + persona(s) so that subscriptions can be created.
 * Body: ICreateUseCasePersona[] — see admin-corev2/src/repositories/usecasepersona.ts
 */
api.linkUseCasePersona = (payload) => {
	return axios
		.post(`${apiProvider.BASE_URL}/usecasepersonas`, payload)
		.then(handleResponse)
		.catch(handleError);
}

/**
 * GET /use-cases/list
 * Fetch the lightweight list of use cases (id + name) for picker dropdowns.
 */
api.fetchUseCases = () => {
	return axios
		.get(`${apiProvider.BASE_URL}/use-cases/list`)
		.then(handleResponse)
		.catch(handleError);
}

/**
 * GET /personas
 * Fetch the list of personas for picker dropdowns.
 */
api.fetchPersonas = () => {
	return axios
		.get(`${apiProvider.BASE_URL}/personas`)
		.then(handleResponse)
		.catch(handleError);
}

/**
 * GET /master-data-tables
 * Fetch the logical names of the buildex master-data tables for the
 * "Master Data Checks" table picker. Returns string[].
 */
api.fetchMasterDataTables = () => {
	return axios
		.get(`${apiProvider.BASE_URL}/master-data-tables`)
		.then(handleResponse)
		.catch(handleError);
}

/**
 * GET /data-elements/persona
 * Fetch DataElementPersona links. Used to detect whether a DE is already attached
 * to any use case + persona so the "Link Use Case" button can hide itself.
 */
api.fetchDeLinks = (dataElementId) => {
	const url = dataElementId
		? `${apiProvider.BASE_URL}/data-elements/persona?dataElementId=${encodeURIComponent(dataElementId)}`
		: `${apiProvider.BASE_URL}/data-elements/persona`;
	return axios
		.get(url)
		.then(handleResponse)
		.catch(handleError);
}

export default api
